/**
 * API Services para Configurações de URA (Unidade de Resposta Audível)
 * VigosCloud - URA System
 * 
 * Baseado no padrão incomingCallsApi.js
 * Todas as chamadas incluem autenticação via token
 */

import axios from 'axios';
import accessLocalStorage from './accessLocalStorage';

/**
 * Cria uma nova configuração de URA
 * @param {Object} configData - Dados da configuração
 * @returns {Promise} Response da API
 */
export const createURAConfig = async (configData) => {
  const { token, ipRequest } = await accessLocalStorage.getUserLocalStorage();
  
  try {
    console.log('🔍 [URA-API] Criando URA:', configData);
    
    const response = await axios.post(
      `${ipRequest}api/config/ura/configs`,
      configData,
      { headers: { Authorization: token } }
    );
    
    console.log('✅ [URA-API] URA criada:', response.data);
    return response.data;
  } catch (error) {
    console.error('❌ [URA-API] Erro ao criar URA:', error);
    return {
      success: false,
      error: error.response?.data?.error || 'Erro ao criar URA',
      message: error.response?.data?.message || error.message,
      details: error.response?.data?.details || null
    };
  }
};

/**
 * Lista todas as configurações de URAs
 * @param {Object} filters - Filtros de busca
 * @returns {Promise} Response da API
 */
export const getAllURAConfigs = async (filters = {}) => {
  const { token, ipRequest } = await accessLocalStorage.getUserLocalStorage();
  
  try {
    console.log('🔍 [URA-API] Listando URAs com filtros:', filters);
    
    // Construir URL com parâmetros
    const url = new URL(`${ipRequest}api/config/ura/configs`);
    
    if (filters.tenant) url.searchParams.append('tenant', filters.tenant);
    if (filters.activeOnly !== undefined) url.searchParams.append('active_only', filters.activeOnly);
    if (filters.limit) url.searchParams.append('limit', filters.limit);
    if (filters.offset) url.searchParams.append('offset', filters.offset);
    
    const response = await axios.get(url.toString(), {
      headers: { Authorization: token }
    });
    
    console.log(`✅ [URA-API] Encontradas ${response.data.data?.length || 0} URAs`);
    return response.data;
  } catch (error) {
    console.error('❌ [URA-API] Erro ao listar URAs:', error);
    return {
      success: false,
      error: error.response?.data?.error || 'Erro ao listar URAs',
      data: []
    };
  }
};

/**
 * Busca configuração de URA por ID
 * @param {number} id - ID da configuração
 * @returns {Promise} Response da API
 */
export const getURAConfigById = async (id) => {
  const { token, ipRequest } = await accessLocalStorage.getUserLocalStorage();
  
  try {
    console.log(`🔍 [URA-API] Buscando URA ID: ${id}`);
    
    const response = await axios.get(
      `${ipRequest}api/config/ura/configs/${id}`,
      { headers: { Authorization: token } }
    );
    
    console.log('✅ [URA-API] URA encontrada');
    return response.data;
  } catch (error) {
    console.error('❌ [URA-API] Erro ao buscar URA:', error);
    return {
      success: false,
      error: error.response?.data?.error || 'Erro ao buscar URA',
      message: error.response?.data?.message || error.message
    };
  }
};

/**
 * Atualiza configuração de URA
 * @param {number} id - ID da configuração
 * @param {Object} updateData - Dados para atualização
 * @returns {Promise} Response da API
 */
export const updateURAConfig = async (id, updateData) => {
  const { token, ipRequest} = await accessLocalStorage.getUserLocalStorage();
  
  try {
    console.log(`🔍 [URA-API] Atualizando URA ID: ${id}`, updateData);
    
    const response = await axios.put(
      `${ipRequest}api/config/ura/configs/${id}`,
      updateData,
      { headers: { Authorization: token } }
    );
    
    console.log('✅ [URA-API] URA atualizada');
    return response.data;
  } catch (error) {
    console.error('❌ [URA-API] Erro ao atualizar URA:', error);
    return {
      success: false,
      error: error.response?.data?.error || 'Erro ao atualizar URA',
      message: error.response?.data?.message || error.message,
      details: error.response?.data?.details || null
    };
  }
};

/**
 * Remove configuração de URA
 * @param {number} id - ID da configuração
 * @returns {Promise} Response da API
 */
export const deleteURAConfig = async (id) => {
  const { token, ipRequest } = await accessLocalStorage.getUserLocalStorage();
  
  try {
    console.log(`🔍 [URA-API] Deletando URA ID: ${id}`);
    
    const response = await axios.delete(
      `${ipRequest}api/config/ura/configs/${id}`,
      { headers: { Authorization: token } }
    );
    
    console.log('✅ [URA-API] URA deletada');
    return response.data;
  } catch (error) {
    console.error('❌ [URA-API] Erro ao deletar URA:', error);
    return {
      success: false,
      error: error.response?.data?.error || 'Erro ao deletar URA',
      message: error.response?.data?.message || error.message
    };
  }
};

/**
 * Valida se context_name está disponível
 * @param {string} tenant - Tenant
 * @param {string} contextName - Nome do contexto
 * @param {number} excludeId - ID da URA a excluir da validação (opcional)
 * @returns {Promise} Response da API
 */
export const validateContextName = async (tenant, contextName, excludeId = null) => {
  const { token, ipRequest } = await accessLocalStorage.getUserLocalStorage();
  
  try {
    console.log(`🔍 [URA-API] Validando context_name: ${contextName} para tenant: ${tenant}`);
    
    const url = new URL(`${ipRequest}api/config/ura/validate-context/${tenant}/${contextName}`);
    if (excludeId) url.searchParams.append('excludeId', excludeId);
    
    const response = await axios.get(url.toString(), {
      headers: { Authorization: token }
    });
    
    console.log('✅ [URA-API] Validação concluída');
    return response.data;
  } catch (error) {
    console.error('❌ [URA-API] Erro ao validar context_name:', error);
    return {
      success: false,
      error: error.response?.data?.error || 'Erro ao validar context_name',
      available: false
    };
  }
};

/**
 * Lista troncos disponíveis para um tenant
 * @param {string} tenant - Tenant (opcional)
 * @returns {Promise} Response da API
 */
export const getAvailableTrunks = async (tenant = null) => {
  const { token, ipRequest } = await accessLocalStorage.getUserLocalStorage();
  
  try {
    console.log(`🔍 [URA-API] Buscando troncos disponíveis para tenant: ${tenant}`);
    
    const url = new URL(`${ipRequest}api/config/incoming/trunks`);
    if (tenant) url.searchParams.append('tenant', tenant);
    
    const response = await axios.get(url.toString(), {
      headers: { Authorization: token }
    });
    
    console.log(`✅ [URA-API] Encontrados ${response.data.data?.length || 0} troncos`);
    return response.data;
  } catch (error) {
    console.error('❌ [URA-API] Erro ao buscar troncos:', error);
    return {
      success: false,
      error: error.response?.data?.error || 'Erro ao buscar troncos',
      data: []
    };
  }
};

// ============================================================================
// FUNÇÕES DE DESENVOLVIMENTO E DEBUG
// ============================================================================

/**
 * Visualiza o dialplan gerado para uma URA (apenas desenvolvimento)
 * @param {number} id - ID da configuração
 * @returns {Promise} Response da API
 */
export const getDialplanDebug = async (id) => {
  const { token, ipRequest } = await accessLocalStorage.getUserLocalStorage();
  
  try {
    console.log(`🔍 [URA-API] Buscando dialplan debug para ID: ${id}`);
    
    const response = await axios.get(
      `${ipRequest}api/config/ura/debug/dialplan/${id}`,
      { headers: { Authorization: token } }
    );
    
    console.log('✅ [URA-API] Dialplan debug obtido');
    return response.data;
  } catch (error) {
    console.error('❌ [URA-API] Erro ao buscar dialplan debug:', error);
    return {
      success: false,
      error: error.response?.data?.error || 'Erro ao buscar dialplan debug',
      data: null
    };
  }
};

/**
 * Força reload do dialplan do Asterisk (apenas desenvolvimento)
 * @returns {Promise} Response da API
 */
export const forceDialplanReload = async () => {
  const { token, ipRequest } = await accessLocalStorage.getUserLocalStorage();
  
  try {
    console.log('🔍 [URA-API] Forçando reload do dialplan');
    
    const response = await axios.post(
      `${ipRequest}api/config/ura/debug/reload`,
      {},
      { headers: { Authorization: token } }
    );
    
    console.log('✅ [URA-API] Reload executado');
    return response.data;
  } catch (error) {
    console.error('❌ [URA-API] Erro ao executar reload:', error);
    return {
      success: false,
      error: error.response?.data?.error || 'Erro ao executar reload',
      message: error.response?.data?.message || error.message
    };
  }
};

// ============================================================================
// FUNÇÕES AUXILIARES
// ============================================================================

/**
 * Valida dados de configuração antes de enviar para API
 * @param {Object} configData - Dados da configuração
 * @returns {Object} { isValid: boolean, errors: string[] }
 */
export const validateURAConfig = (configData) => {
  const errors = [];
  
  // Validações obrigatórias
  if (!configData.tenant || configData.tenant.trim().length === 0) {
    errors.push('Tenant é obrigatório');
  }
  
  if (!configData.context_name || configData.context_name.trim().length === 0) {
    errors.push('Nome do contexto é obrigatório');
  }
  
  // Validar context_name format
  if (configData.context_name && !/^[a-zA-Z0-9_-]+$/.test(configData.context_name)) {
    errors.push('Nome do contexto deve conter apenas letras, números, _ e -');
  }
  
  // Validações do menu
  if (configData.menu_audio_enabled) {
    if (!configData.menu_audio_file || configData.menu_audio_file.trim() === '') {
      errors.push('Arquivo de áudio do menu é obrigatório quando áudio está habilitado');
    }
    if (!configData.menu_audio_type) {
      errors.push('Tipo de áudio do menu é obrigatório quando áudio está habilitado');
    }
    if (!configData.menu_audio_accountcode) {
      errors.push('AccountCode do áudio do menu é obrigatório quando áudio está habilitado');
    }
  }
  
  // Validar WaitExten
  if (configData.waitexten_timeout && (configData.waitexten_timeout < 5 || configData.waitexten_timeout > 60)) {
    errors.push('Timeout do WaitExten deve estar entre 5 e 60 segundos');
  }
  
  // Validar destino externo do waitexten
  if (configData.waitexten_destination_type === 'external') {
    if (!configData.waitexten_external_trunk || configData.waitexten_external_trunk.trim() === '') {
      errors.push('Tronco do destino timeout é obrigatório');
    }
    if (!configData.waitexten_external_number || configData.waitexten_external_number.trim() === '') {
      errors.push('Número do destino timeout é obrigatório');
    }
  }
  
  // Validar time conditions do menu
  if (configData.menu_gotoiftime_rules && Array.isArray(configData.menu_gotoiftime_rules)) {
    configData.menu_gotoiftime_rules.forEach((rule, index) => {
      if (!rule.time_range || rule.time_range.trim() === '') {
        errors.push(`Time range da regra ${index + 1} do menu é obrigatório`);
      }
      if (!rule.days_of_week || rule.days_of_week.trim() === '') {
        errors.push(`Dias da semana da regra ${index + 1} do menu é obrigatório`);
      }
      if (!rule.context_name || rule.context_name.trim() === '') {
        errors.push(`Contexto de destino da regra ${index + 1} do menu é obrigatório`);
      }
      if (!rule.extension_name || rule.extension_name.trim() === '') {
        errors.push(`Extensão de destino da regra ${index + 1} do menu é obrigatória`);
      }
    });
  }
  
  // Validar extensões
  if (!configData.extensions || !Array.isArray(configData.extensions) || configData.extensions.length === 0) {
    errors.push('Pelo menos uma extensão deve ser configurada');
  }
  
  if (configData.extensions && Array.isArray(configData.extensions)) {
    // Verificar extension_pattern únicos
    const patterns = configData.extensions.map(ext => ext.extension_pattern);
    const duplicates = patterns.filter((item, index) => patterns.indexOf(item) !== index);
    
    if (duplicates.length > 0) {
      errors.push(`Extensões duplicadas encontradas: ${duplicates.join(', ')}`);
    }
    
    configData.extensions.forEach((ext, index) => {
      if (!ext.extension_pattern || ext.extension_pattern.trim().length === 0) {
        errors.push(`Padrão da extensão ${index + 1} é obrigatório`);
      }
      
      if (ext.extension_pattern && !/^[a-zA-Z0-9*#]+$/.test(ext.extension_pattern)) {
        errors.push(`Padrão da extensão ${index + 1} deve conter apenas letras, números, * ou #`);
      }
      
      if (!ext.destination_type_main) {
        errors.push(`Tipo de destino da extensão ${index + 1} é obrigatório`);
      }
      
      // Validar destino externo da extensão
      if (ext.destination_type_main === 'external') {
        if (!ext.external_trunk || ext.external_trunk.trim() === '') {
          errors.push(`Tronco da extensão ${index + 1} é obrigatório`);
        }
        if (!ext.external_number || ext.external_number.trim() === '') {
          errors.push(`Número da extensão ${index + 1} é obrigatório`);
        }
      }
      
      // Validar áudio da extensão se habilitado
      if (ext.audio_enabled) {
        if (!ext.audio_file || ext.audio_file.trim() === '') {
          errors.push(`Arquivo de áudio da extensão ${index + 1} é obrigatório quando áudio está habilitado`);
        }
        if (!ext.audio_type) {
          errors.push(`Tipo de áudio da extensão ${index + 1} é obrigatório quando áudio está habilitado`);
        }
      }
      
      // Validar time conditions da extensão
      if (ext.gotoiftime_rules && Array.isArray(ext.gotoiftime_rules)) {
        ext.gotoiftime_rules.forEach((rule, ruleIndex) => {
          if (!rule.time_range || rule.time_range.trim() === '') {
            errors.push(`Time range da regra ${ruleIndex + 1} da extensão ${index + 1} é obrigatório`);
          }
          if (!rule.days_of_week || rule.days_of_week.trim() === '') {
            errors.push(`Dias da semana da regra ${ruleIndex + 1} da extensão ${index + 1} é obrigatório`);
          }
          if (!rule.context_name || rule.context_name.trim() === '') {
            errors.push(`Contexto de destino da regra ${ruleIndex + 1} da extensão ${index + 1} é obrigatório`);
          }
          if (!rule.extension_name || rule.extension_name.trim() === '') {
            errors.push(`Extensão de destino da regra ${ruleIndex + 1} da extensão ${index + 1} é obrigatória`);
          }
        });
      }
      
      // Validar overflows da extensão
      if (ext.overflows && Array.isArray(ext.overflows)) {
        ext.overflows.forEach((overflow, ovfIndex) => {
          if (overflow.timeout_seconds && (overflow.timeout_seconds < 1 || overflow.timeout_seconds > 300)) {
            errors.push(`Timeout do overflow ${ovfIndex + 1} da extensão ${index + 1} deve estar entre 1 e 300 segundos`);
          }
          
          // Validar destino externo do overflow
          if (overflow.destination_type === 'external') {
            if (!overflow.overflow_external_trunk || overflow.overflow_external_trunk.trim() === '') {
              errors.push(`Tronco do overflow ${ovfIndex + 1} da extensão ${index + 1} é obrigatório`);
            }
            if (!overflow.overflow_external_number || overflow.overflow_external_number.trim() === '') {
              errors.push(`Número do overflow ${ovfIndex + 1} da extensão ${index + 1} é obrigatório`);
            }
          }
        });
      }
    });
  }
  
  // Validar overflows do waitexten (timeout do menu)
  if (configData.waitexten_overflows && Array.isArray(configData.waitexten_overflows)) {
    configData.waitexten_overflows.forEach((overflow, ovfIndex) => {
      if (overflow.timeout_seconds && (overflow.timeout_seconds < 1 || overflow.timeout_seconds > 300)) {
        errors.push(`Timeout do transbordo ${ovfIndex + 1} do menu deve estar entre 1 e 300 segundos`);
      }
      
      if (overflow.destination_type === 'external') {
        if (!overflow.overflow_external_trunk || overflow.overflow_external_trunk.trim() === '') {
          errors.push(`Tronco do transbordo ${ovfIndex + 1} do menu é obrigatório`);
        }
        if (!overflow.overflow_external_number || overflow.overflow_external_number.trim() === '') {
          errors.push(`Número do transbordo ${ovfIndex + 1} do menu é obrigatório`);
        }
      }
    });
  }
  
  return {
    isValid: errors.length === 0,
    errors
  };
};

/**
 * Formata dados de configuração para envio à API
 * @param {Object} formData - Dados do formulário
 * @returns {Object} Dados formatados
 */
export const formatConfigDataForAPI = (formData) => {
  const formatted = {
    ...formData,
    // Garantir que valores booleanos sejam enviados corretamente
    menu_audio_enabled: Boolean(formData.menu_audio_enabled),
    cycle_enabled: Boolean(formData.cycle_enabled),
    active: formData.active !== false,
    
    // Limpar campos vazios
    context_name: formData.context_name?.trim() || null,
    description: formData.description?.trim() || null,
    menu_extension: formData.menu_extension?.trim() || 'menu',
    
    // Garantir que valores numéricos sejam números
    waitexten_timeout: parseInt(formData.waitexten_timeout) || 10,
    max_retries: parseInt(formData.max_retries) || 3,
    
    // Garantir que arrays existam
    menu_gotoiftime_rules: Array.isArray(formData.menu_gotoiftime_rules) ? formData.menu_gotoiftime_rules : [],
    waitexten_overflows: Array.isArray(formData.waitexten_overflows) ? formData.waitexten_overflows : [],
    extensions: Array.isArray(formData.extensions) ? formData.extensions : []
  };
  
  // Converter destino externo do waitexten (trunk + number → destination)
  if (formData.waitexten_destination_type === 'external') {
    if (formData.waitexten_external_trunk && formData.waitexten_external_number) {
      formatted.waitexten_destination = `${formData.waitexten_external_trunk}/${formData.waitexten_external_number}`;
    }
    delete formatted.waitexten_external_trunk;
    delete formatted.waitexten_external_number;
  }
  
  // Formatar overflows do waitexten
  if (formatted.waitexten_overflows && formatted.waitexten_overflows.length > 0) {
    formatted.waitexten_overflows = formatted.waitexten_overflows.map(overflow => {
      const formattedOverflow = { ...overflow };
      
      if (overflow.destination_type === 'external') {
        if (overflow.overflow_external_trunk && overflow.overflow_external_number) {
          formattedOverflow.destination_overflow = `${overflow.overflow_external_trunk}/${overflow.overflow_external_number}`;
        }
        delete formattedOverflow.overflow_external_trunk;
        delete formattedOverflow.overflow_external_number;
      }
      
      return formattedOverflow;
    });
  }
  
  // Formatar extensões
  formatted.extensions = formatted.extensions.map(ext => {
    const formattedExt = {
      ...ext,
      audio_enabled: Boolean(ext.audio_enabled),
      gotoiftime_rules: Array.isArray(ext.gotoiftime_rules) ? ext.gotoiftime_rules : [],
      overflows: Array.isArray(ext.overflows) ? ext.overflows : []
    };
    
    // Converter destino externo da extensão (trunk + number → destination_main)
    if (ext.destination_type_main === 'external') {
      if (ext.external_trunk && ext.external_number) {
        formattedExt.destination_main = `${ext.external_trunk}/${ext.external_number}`;
      }
      // Remover campos temporários
      delete formattedExt.external_trunk;
      delete formattedExt.external_number;
    }
    
    // Converter destinos externos dos overflows
    if (formattedExt.overflows && formattedExt.overflows.length > 0) {
      formattedExt.overflows = formattedExt.overflows.map(overflow => {
        const formattedOverflow = { ...overflow };
        
        if (overflow.destination_type === 'external') {
          if (overflow.overflow_external_trunk && overflow.overflow_external_number) {
            formattedOverflow.destination_overflow = `${overflow.overflow_external_trunk}/${overflow.overflow_external_number}`;
          }
          // Remover campos temporários
          delete formattedOverflow.overflow_external_trunk;
          delete formattedOverflow.overflow_external_number;
        }
        
        return formattedOverflow;
      });
    }
    
    return formattedExt;
  });
  
  // Remover campos undefined/null desnecessários
  Object.keys(formatted).forEach(key => {
    if (formatted[key] === undefined || formatted[key] === '') {
      delete formatted[key];
    }
  });
  
  return formatted;
};

/**
 * Gera nome de contexto sugerido baseado no tenant
 * @param {string} tenant - Tenant
 * @param {string} uraName - Nome opcional da URA
 * @returns {string} Nome do contexto sugerido
 */
export const generateContextName = (tenant, uraName = '') => {
  const baseName = 'ura';
  const normalizedName = uraName
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]/g, '')
    .substring(0, 20);
  
  if (normalizedName) {
    return `${baseName}-${normalizedName}-${tenant}`;
  }
  
  return `${baseName}-${tenant}`;
};

export default {
  createURAConfig,
  getAllURAConfigs,
  getURAConfigById,
  updateURAConfig,
  deleteURAConfig,
  validateContextName,
  getAvailableTrunks,
  getDialplanDebug,
  forceDialplanReload,
  validateURAConfig,
  formatConfigDataForAPI,
  generateContextName
};

